import React, { Fragment } from 'react'
import { useIntl } from 'intl'
import { type PriceChannel, priceChannels, PriceSelectionProvider } from 'modules/priceSelection'
import { useProductGiftPromo } from 'modules/products'

import { Text } from 'components/dataDisplay'

import OrderItemBase from 'compositions/orderItems/OrderItemBase/OrderItemBase'
import OrderProductBase from 'compositions/orderItems/OrderProductBase/OrderProductBase'
import OrderItemRemoveButton from 'compositions/orderItems/OrderItemRemoveButton/OrderItemRemoveButton'
import OrderItemQuantity from 'compositions/orderItems/OrderItemQuantity/OrderItemQuantity'

import PrivateSaleItems from './components/PrivateSaleItems/PrivateSaleItems'
import AcademyCourseOrderItem from './components/AcademyCourseOrderItem/AcademyCourseOrderItem'

import messages from './messages'


type ProductOrderItemProps = {
  className?: string
  data: OrderItems.Product
  withRemoveButton?: boolean
}

const ProductOrderItem: React.FunctionComponent<ProductOrderItemProps> = (props) => {
  const { className, data, withRemoveButton } = props

  const intl = useIntl()
  const { subTotal, isFetching } = useProductGiftPromo()

  const items = data.offerProducts?.length ? [ data, ...data.offerProducts ] : [ data ]

  const { id, uid, brand, category, type, fullName, gender, name, offer, size, variety } = data
  const { name: varietyName } = variety || {}

  const productLineItemUid = uid
  const isTossIn = offer === 'TOSSIN' || offer === 'TOSSIN_EMAIL'
  const isPrivateSale = type === 'FlashSale'
  const isAcademyCourse = type === 'AcademyCourse'

  const nameLine = varietyName ? { ...messages.varietyProductName, values: { name, variety: varietyName } } : name

  const removeMetadata: Analytics.Events['Cart product remove'] = {
    productType: 'Ecommerce',
    placement: 'Drawer cart',
    productId: id,
    productUid: uid,
    productBrand: brand,
    productFullName: fullName,
    productCategory: category,
    productVolume: size,
    productGender: gender,
  }

  let priceChannel: PriceChannel

  if (isPrivateSale) {
    priceChannel = priceChannels.privateSale
  }
  else if (isTossIn) {
    priceChannel = priceChannels.tossIn
  }

  return (
    <PriceSelectionProvider channel={priceChannel}>
      <OrderItemBase className={className} data-testid="orderProduct">
        {
          withRemoveButton && (
            <OrderItemRemoveButton
              input={{
                productItems: [
                  {
                    productLineItemUid,
                  },
                ],
              }}
              metadata={removeMetadata}
            />
          )
        }
        {
          items.map((item, index) => {
            const { uid, image, rebrandImage, brand, name, price, size, quantity, type, typeGroup } = item
            const isFirst = index === 0
            const dataTestId = isFirst ? 'primaryProduct' : 'offerProduct'

            if (isAcademyCourse && isFirst) {
              return (
                <Fragment key={uid}>
                  <AcademyCourseOrderItem
                    className={index > 0 ? 'mt-24' : null}
                    data={data}
                    withRemoveButton={withRemoveButton}
                    data-testid={dataTestId}
                  />
                  {
                    items.length > 1 && (
                      <hr className="mt-24 bg-gray-30" />
                    )
                  }
                </Fragment>
              )
            }

            const withQuantityEditor = withRemoveButton && isFirst && !isAcademyCourse
            const withOneTimeLabel = isFirst && !isTossIn && !isPrivateSale && !isAcademyCourse
            const withSize = !isPrivateSale && !isAcademyCourse && typeGroup !== 'PerfumeCase'
            const isAcademyCourseKit = type === 'AcademyCourseKit'

            let label: Intl.Message = messages.oneTime

            if (offer === 'GIFT_FOR_SUBTOTAL') {
              label = isFetching || !subTotal
                ? messages.giftWithSpendThresholdFetching
                : {
                  ...messages.giftWithSpendThreshold,
                  values: { price: intl.formatPrice(subTotal, { removeZeroCents: true }) },
                }
            }

            return (
              <OrderProductBase
                key={uid}
                className={index > 0 ? 'mt-24' : null}
                image={image}
                rebrandImage={rebrandImage}
                name={isFirst ? nameLine : name}
                brand={isAcademyCourseKit ? null : brand}
                quantity={withQuantityEditor && typeGroup !== 'PerfumeCase' ? null : quantity}
                typeGroup={typeGroup}
                size={withSize ? size : null}
                price={price}
                afterNode={(
                  withQuantityEditor && (
                    <OrderItemQuantity
                      className="mt-8"
                      productLineItemUid={productLineItemUid}
                      value={quantity}
                      metadata={removeMetadata}
                    />
                  )
                )}
                beforeNode={(
                  withOneTimeLabel && (
                    <Text
                      className="mb-8"
                      message={label}
                      style="h9"
                      html
                    />
                  )
                )}
                data-testid={dataTestId}
              />
            )
          })
        }
        {
          isPrivateSale && (
            <PrivateSaleItems product={data} />
          )
        }
      </OrderItemBase>
    </PriceSelectionProvider>
  )
}


export default ProductOrderItem
